import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

/**
 * Profile Image Service
 * Handles profile image upload, retrieval, and deletion via BFF
 */
class ProfileImageService {
  /**
   * Get the BFF base URL
   */
  getBffUrl() {
    return config.bffUrl || window.location.origin;
  }

  /**
   * Build API URL - use relative path in dev, absolute in production
   * @param {string} path - API path (e.g., '/api/user/profile-image')
   * @returns {string} - Full or relative URL
   */
  buildApiUrl(path) {
    // If bffUrl is configured (production), use absolute URL
    if (config.bffUrl) {
      return `${config.bffUrl}${path}`;
    }
    // In development (bffUrl is null), use relative path for Vite proxy
    return path;
  }

  /**
   * Get user ID from storage or auth context
   */
  getUserId() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.sub || user.id;
  }

  /**
   * Upload a profile image
   * @param {File} file - Image file to upload
   * @param {string} userId - Optional user ID (defaults to current user)
   * @returns {Promise<{success: boolean, fileId: string, message: string}>}
   */
  async uploadProfileImage(file, userId = null) {
    const effectiveUserId = userId || this.getUserId();
    
    if (!effectiveUserId) {
      throw new Error('User ID not available. Please log in.');
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
    }

    // Validate file size (5MB)
    if (file.size > 5242880) {
      throw new Error('Image must be less than 5MB.');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await getAuthorizedFetch()(this.buildApiUrl('/api/user/profile-image'), {
      method: 'POST',
      headers: {
        ...getBffHeaders(),
        'mia-userid': effectiveUserId,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({message: 'Upload failed'}));
      throw new Error(error.message || 'Failed to upload profile image');
    }

    return response.json();
  }

  /**
   * Get profile image URL for a user
   * @param {string} userId - User ID (optional, defaults to current user)
   * @returns {string} - Image URL
   */
  getProfileImageUrl(userId = null) {
    const effectiveUserId = userId || this.getUserId();
    
    if (!effectiveUserId) {
      return null;
    }

    // Add timestamp to prevent caching issues
    const timestamp = Date.now();
    return this.buildApiUrl(`/api/user/profile-image/${effectiveUserId}?t=${timestamp}`);
  }

  /**
   * Delete profile image
   * @param {string} userId - Optional user ID (defaults to current user)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteProfileImage(userId = null) {
    const effectiveUserId = userId || this.getUserId();
    
    if (!effectiveUserId) {
      throw new Error('User ID not available. Please log in.');
    }

    const response = await getAuthorizedFetch()(this.buildApiUrl('/api/user/profile-image'), {
      method: 'DELETE',
      headers: {
        ...getBffHeaders(),
        'mia-userid': effectiveUserId,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({message: 'Delete failed'}));
      throw new Error(error.message || 'Failed to delete profile image');
    }

    return response.json();
  }

  /**
   * Check if a profile image exists
   * @param {string} userId - User ID (optional, defaults to current user)
   * @returns {Promise<boolean>}
   */
  async hasProfileImage(userId = null) {
    const effectiveUserId = userId || this.getUserId();
    
    if (!effectiveUserId) {
      return false;
    }

    try {
      const response = await getAuthorizedFetch()(this.buildApiUrl(`/api/user/profile-image/${effectiveUserId}`), {
        method: 'HEAD',
        headers: getBffHeaders(),
      });

      return response.ok;
    } catch (error) {
      // 404 is expected when user has no profile image - don't log as error
      if (error.status !== 404) {
        console.debug('Error checking profile image:', error);
      }
      return false;
    }
  }
}

export default new ProfileImageService();
